import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { getArticle } from "@/lib/api";
import { analytics } from "@/lib/analytics";
import BrandBand from "@/components/BrandBand";
import Seo, { articleLd, breadcrumbLd } from "@/components/Seo";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);

  useEffect(() => {
    setArticle(null);
    getArticle(slug).then(setArticle).catch(() => setArticle({ notFound: true }));
    window.scrollTo(0, 0);
  }, [slug]);

  // 75%-scroll read tracking
  useEffect(() => {
    if (!article || article.notFound) return;
    let fired = false;
    const onScroll = () => {
      if (fired) return;
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (total > 0 && scrolled / total >= 0.75) {
        analytics.articleRead(article.slug);
        fired = true;
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [article]);

  if (!article) return <div className="section max-w-[900px] mx-auto px-6 opacity-60">Loading…</div>;
  if (article.notFound) return <div className="section max-w-[900px] mx-auto px-6 font-serif-editorial text-3xl">Article not found.</div>;

  const ld = [
    articleLd(article),
    breadcrumbLd([
      { name: "Home", path: "/" },
      { name: "Learn", path: "/learn" },
      { name: article.title, path: `/article/${article.slug}` },
    ]),
  ];

  return (
    <div data-testid="article-page">
      <Seo
        title={article.title}
        description={article.excerpt}
        path={`/article/${article.slug}`}
        image={article.image}
        type="article"
        jsonLd={ld}
      />

      <article>
        <header className="section pb-8">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-8 md:col-start-2">
              <div className="overline">{article.kicker} · {article.reading_time}</div>
              <h1 className="font-serif-editorial tight-track mt-6" style={{ fontSize: "clamp(2.4rem, 5.5vw, 5rem)", lineHeight: 1 }} data-testid="article-title">
                {article.title}
              </h1>
              <p className="mt-8 text-xl font-serif-editorial" style={{ maxWidth: "42rem", lineHeight: 1.4 }}>
                {article.excerpt}
              </p>
              <div className="mt-10 flex flex-wrap gap-6 overline">
                <span>{article.author}</span>
                <span>{article.date}</span>
              </div>
            </div>
          </div>
        </header>

        {article.image ? (
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="aspect-[16/8] overflow-hidden">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          </div>
        ) : null}

        <div className="section pt-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7 md:col-start-3" data-testid="article-body">
              <ReactMarkdown
                components={{
                  h1: ({node, ...p}) => <h2 className="font-serif-editorial mt-14 mb-6" style={{ fontSize: "2.4rem", lineHeight: 1.1 }} {...p} />,
                  h2: ({node, ...p}) => <h2 className="font-serif-editorial mt-14 mb-6" style={{ fontSize: "2rem", lineHeight: 1.15 }} {...p} />,
                  h3: ({node, ...p}) => <h3 className="font-serif-editorial mt-10 mb-4" style={{ fontSize: "1.5rem" }} {...p} />,
                  p: ({node, ...p}) => <p className="text-lg leading-relaxed mt-6" style={{ maxWidth: "40rem" }} {...p} />,
                  ul: ({node, ...p}) => <ul className="list-disc pl-6 mt-6 space-y-2 text-lg" style={{ maxWidth: "40rem" }} {...p} />,
                  ol: ({node, ...p}) => <ol className="list-decimal pl-6 mt-6 space-y-2 text-lg" style={{ maxWidth: "40rem" }} {...p} />,
                  li: ({node, ...p}) => <li style={{ lineHeight: 1.6 }} {...p} />,
                  a: ({node, href = "", ...p}) => {
                    const isInternal = href.startsWith("/") || href.includes("siboinstitute.com");
                    const extraProps = isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" };
                    return <a href={href} className="editorial-link" style={{ color: "var(--olive)", textDecoration: "underline", textUnderlineOffset: "3px" }} {...extraProps} {...p} />;
                  },
                  blockquote: ({node, ...p}) => <blockquote className="border-l-2 pl-6 my-8 font-serif-editorial italic" style={{ borderColor: "var(--sage)", fontSize: "1.3rem", lineHeight: 1.5 }} {...p} />,
                  strong: ({node, ...p}) => <strong style={{ fontWeight: 600 }} {...p} />,
                  img: ({node, ...p}) => (
                    <figure className="my-10" style={{ maxWidth: "42rem" }}>
                      <img {...p} alt={p.alt || ""} style={{ width: "100%", height: "auto", display: "block" }} />
                      {p.alt && <figcaption className="overline mt-3 opacity-70">{p.alt}</figcaption>}
                    </figure>
                  ),
                }}
              >
                {article.body || ""}
              </ReactMarkdown>

              <div className="rule mt-20" />
              <div className="mt-10 overline">SIBO Institute Editorial</div>
            </div>
          </div>
        </div>

        {article.related && article.related.length > 0 && (
          <section className="section pt-0" style={{ backgroundColor: "var(--sand)" }} data-testid="related-articles">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24">
              <div className="overline">Continue reading</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-12">
                {article.related.map((a) => (
                  <Link to={`/article/${a.slug}`} key={a.slug} className="group" data-testid={`related-${a.slug}`}>
                    <div className="aspect-[4/5] overflow-hidden">
                      <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="overline mt-6">{a.kicker}</div>
                    <div className="font-serif-editorial mt-3" style={{ fontSize: "1.5rem", lineHeight: 1.2 }}>{a.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
      <BrandBand />
    </div>
  );
}
